/**
 * Re-encodes a video as SDR through the compositor, so HDR media is tone-mapped exactly like it is
 * in preview and export (the browser's own HDR-to-SDR conversion, applied whenever a frame is drawn
 * into a 2D canvas, looks different). Used for proxies of HDR media.
 */
import type { FrameDescription } from '@ve/core';
import {
  BufferTarget,
  CanvasSource,
  Mp4OutputFormat,
  Output,
  VideoSampleSink,
  type VideoCodec,
} from 'mediabunny';
import { Compositor, type LayerTexture } from '../compositor/compositor.ts';
import { transferOf } from '../media/color.ts';
import { MediaInputs } from './frame-reader.ts';
import type { MediaInput } from '../types.ts';

export interface SdrTranscodeOptions {
  width: number;
  height: number;
  codec: VideoCodec;
  bitrate: number;
  keyFrameInterval: number;
  signal?: AbortSignal;
  onProgress?: (progress: number) => void;
}

export async function transcodeToSdr(media: MediaInput, options: SdrTranscodeOptions): Promise<Blob> {
  const { width, height, signal } = options;
  const inputs = new MediaInputs();
  const canvas = new OffscreenCanvas(width, height);
  const compositor = new Compositor(canvas, { preserveDrawingBuffer: true, highQuality: true });
  const target = new BufferTarget();
  const output = new Output({ format: new Mp4OutputFormat({ fastStart: 'in-memory' }), target });
  try {
    const track = await inputs.videoTrack(media);
    const transfer = transferOf(await track.getColorSpace());
    const duration = await track.computeDuration();
    const videoSource = new CanvasSource(canvas, {
      codec: options.codec,
      bitrate: options.bitrate,
      keyFrameInterval: options.keyFrameInterval,
    });
    output.addVideoTrack(videoSource);
    await output.start();

    const desc: FrameDescription = {
      frame: 0,
      width,
      height,
      background: '#000000',
      layers: [
        {
          kind: 'layer',
          key: 'video',
          clipId: 'video',
          source: { type: 'video', mediaId: 'video', time: 0 },
          fit: 'stretch',
          blendMode: 'normal',
          opacity: 1,
          transform: {
            x: 0,
            y: 0,
            scaleX: 1,
            scaleY: 1,
            rotation: 0,
            crop: { left: 0, right: 0, top: 0, bottom: 0 },
          },
        },
      ],
    };
    for await (const sample of new VideoSampleSink(track).samples()) {
      try {
        if (signal?.aborted) throw new DOMException('Transcoding was cancelled', 'AbortError');
        const frame = sample.toVideoFrame();
        const texture: LayerTexture = { source: frame, width, height, transfer, rotation: sample.rotation };
        try {
          compositor.render(desc, { getTexture: () => texture });
        } finally {
          frame.close();
        }
        await videoSource.add(sample.timestamp, sample.duration);
        if (duration > 0) options.onProgress?.(Math.min(1, (sample.timestamp + sample.duration) / duration));
      } finally {
        sample.close();
      }
    }
    videoSource.close();
    await output.finalize();
    if (!target.buffer) throw new Error('Transcoding produced no output');
    return new Blob([target.buffer], { type: 'video/mp4' });
  } catch (err) {
    if (output.state !== 'finalized' && output.state !== 'canceled')
      await output.cancel().catch(() => undefined);
    throw err;
  } finally {
    inputs.dispose();
    compositor.dispose();
  }
}
